import { ChevronDown, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, type Location } from "react-router-dom";
import type { NavigatorRow, NavigatorSpec } from "@/app/destinations";
import { cn } from "@/lib/cn";

export const PANEL_DEFAULT_WIDTH = 252;
export const PANEL_MIN_WIDTH = 180;
export const PANEL_MAX_WIDTH = 420;

/**
 * Frame 3 of UI-3 — collapsible, resizable, and rendered entirely from the active
 * destination's `NavigatorSpec`. No page owns a bespoke sidebar; a later milestone
 * adds rows and counts to a descriptor and this renders them.
 */
export function NavigatorPanel({
  spec,
  width,
  onWidthChange,
}: {
  spec: NavigatorSpec;
  width: number;
  onWidthChange: (width: number) => void;
}) {
  const [filter, setFilter] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // The filter is per-destination; carrying one page's text to the next would be a
  // small, confusing kind of memory.
  useEffect(() => setFilter(""), [spec.title]);

  // A fresh search starts from each nested row's own default (collapsed, unless the
  // search forces it open below) rather than whatever an earlier search left expanded.
  useEffect(() => setExpandedRows({}), [filter]);

  const { sections, forceExpanded } = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return { sections: spec.sections, forceExpanded: new Set<string>() };

    const forceExpanded = new Set<string>();

    // A recording matches by its own name, or by keeping only the meetings that match
    // theirs — and a row kept only because a child matched is opened for the searcher,
    // since a matching meeting nobody can see is the same as no match at all.
    const filterRow = (row: NavigatorRow): NavigatorRow | null => {
      const selfMatch = row.label.toLowerCase().includes(needle);
      if (!row.rows?.length) return selfMatch ? row : null;
      if (selfMatch) return row;

      const children = row.rows.map(filterRow).filter((child): child is NavigatorRow => child !== null);
      if (children.length === 0) return null;

      forceExpanded.add(row.id);
      return { ...row, rows: children };
    };

    const sections = spec.sections
      .map((section) => ({
        ...section,
        rows: section.rows.map(filterRow).filter((row): row is NavigatorRow => row !== null),
      }))
      .filter((section) => section.rows.length > 0);

    return { sections, forceExpanded };
  }, [spec.sections, filter]);

  const toggleRow = useCallback(
    (id: string) => setExpandedRows((current) => ({ ...current, [id]: !(current[id] ?? forceExpanded.has(id)) })),
    [forceExpanded],
  );

  return (
    <div
      className="relative flex shrink-0 flex-col bg-[var(--sidebar)]"
      style={{ width }}
      data-testid="navigator-panel"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-[var(--space-3)] overflow-y-auto px-[var(--space-3)] py-[var(--space-4)]">
        <p className="px-1 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          {spec.title}
        </p>

        {spec.subtitle && (
          <p className="px-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">{spec.subtitle}</p>
        )}

        {spec.filter && (
          <div className="relative">
            <Search
              size={12}
              strokeWidth={2}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
            />
            <input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder={spec.filter}
              aria-label={spec.filter}
              className="h-7 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] pl-7 pr-7 text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)] focus:border-[var(--ring)]"
            />
            {filter && (
              <button
                type="button"
                onClick={() => setFilter("")}
                aria-label="Clear filter"
                className="absolute right-1.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-[var(--radius-full)] bg-[var(--surface-2)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <X size={10} strokeWidth={2.5} />
              </button>
            )}
          </div>
        )}

        {sections.map((section) => {
          const isCollapsed = collapsed[section.id] ?? false;

          return (
            <div key={section.id}>
              <button
                type="button"
                onClick={() => setCollapsed((c) => ({ ...c, [section.id]: !isCollapsed }))}
                aria-expanded={!isCollapsed}
                className="flex w-full items-center gap-1 px-1 py-1 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
              >
                <ChevronDown
                  size={11}
                  strokeWidth={2.5}
                  className={cn("transition-transform duration-[var(--duration-fast)]", isCollapsed && "-rotate-90")}
                />
                <span className="truncate">{section.label}</span>
              </button>

              {!isCollapsed && (
                <div className="mt-0.5 flex flex-col">
                  {section.rows.length === 0 ? (
                    // Says nothing is here rather than showing an invented row.
                    <p className="px-[17px] py-1 text-[length:var(--text-2xs)] italic text-[var(--text-muted)]">
                      Nothing yet
                    </p>
                  ) : (
                    section.rows.map((row) => (
                      <NavigatorRowItem
                        key={row.id}
                        row={row}
                        expandedRows={expandedRows}
                        forceExpanded={forceExpanded}
                        onToggle={toggleRow}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ResizeHandle width={width} onWidthChange={onWidthChange} />
    </div>
  );
}

function NavigatorRowItem({
  row,
  expandedRows,
  forceExpanded,
  onToggle,
  depth = 0,
}: {
  row: NavigatorRow;
  expandedRows: Record<string, boolean>;
  forceExpanded: Set<string>;
  onToggle: (id: string) => void;
  depth?: number;
}) {
  const location = useLocation();
  const hasChildren = (row.rows?.length ?? 0) > 0;
  const isExpanded = expandedRows[row.id] ?? forceExpanded.has(row.id);

  const content = (
    <>
      {row.dot && (
        <span
          aria-hidden
          className="h-1.5 w-1.5 shrink-0 rounded-[var(--radius-full)]"
          style={{ background: row.dot }}
        />
      )}
      <span className="truncate">{row.label}</span>
      {row.count !== undefined && (
        <span className="ml-auto text-[length:var(--text-2xs)] tabular-nums text-[var(--text-muted)]">{row.count}</span>
      )}
    </>
  );

  const shared = cn(
    "flex min-w-0 flex-1 items-center gap-[var(--space-2)] rounded-[var(--radius-md)] py-1.5 pr-2 text-left text-[length:var(--text-xs)] transition-[filter,background-color] duration-[var(--duration-fast)] hover:brightness-[1.35]",
    depth === 0 ? "pl-[17px]" : "pl-[6px]",
  );

  const link = row.to ? (
    <Link
      to={row.to}
      aria-current={isActiveRow(row.to, location) ? "page" : undefined}
      className={cn(
        shared,
        isActiveRow(row.to, location)
          ? "bg-[var(--sidebar-accent)] font-semibold text-[var(--text-primary)]"
          : "text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)]",
      )}
    >
      {content}
    </Link>
  ) : (
    // Rows without a destination are filters that the milestone owning this page wires
    // up. Disabled rather than dead: a row that looks clickable and is not is worse.
    <span className={cn(shared, "cursor-default text-[var(--text-muted)] opacity-70")}>{content}</span>
  );

  return (
    <div>
      <div className="flex items-center" style={depth > 0 ? { paddingLeft: 17 + depth * 14 } : undefined}>
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(row.id)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? `Collapse ${row.label}` : `Expand ${row.label}`}
            className="flex h-full shrink-0 items-center justify-center px-1 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
          >
            <ChevronDown
              size={10}
              strokeWidth={2.5}
              className={cn("transition-transform duration-[var(--duration-fast)]", !isExpanded && "-rotate-90")}
            />
          </button>
        ) : (
          depth === 0 && <span className="w-[17px] shrink-0" aria-hidden />
        )}
        {link}
      </div>

      {hasChildren && isExpanded && (
        <div className="flex flex-col">
          {row.rows!.map((child) => (
            <NavigatorRowItem
              key={child.id}
              row={child}
              expandedRows={expandedRows}
              forceExpanded={forceExpanded}
              onToggle={onToggle}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A row is active when the browser is at exactly its target — path *and* query.
 *
 * Query is part of it, unlike the default NavLink match, because the entities "By type"
 * rows differ only by a `?type=` and would otherwise all light up at once on `/entities`.
 * Comparing the normalized query strings keeps every existing row (all of which carry no
 * query) matching on the path exactly as before.
 */
function isActiveRow(to: string, location: Location): boolean {
  const [path, query = ""] = to.split("?");
  if (location.pathname !== path) return false;

  const wanted = new URLSearchParams(query).toString();
  const here = new URLSearchParams(location.search).toString();
  return wanted === here;
}

/** 5 px hit area on the right edge, per the handoff. */
function ResizeHandle({ width, onWidthChange }: { width: number; onWidthChange: (width: number) => void }) {
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      startX.current = event.clientX;
      startWidth.current = width;

      const onMove = (move: PointerEvent) => {
        const next = startWidth.current + (move.clientX - startX.current);
        onWidthChange(Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, next)));
      };

      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      // Held on the body so the drag survives the pointer leaving the 5 px strip.
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [width, onWidthChange],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize the navigator panel"
      onPointerDown={onPointerDown}
      onDoubleClick={() => onWidthChange(PANEL_DEFAULT_WIDTH)}
      className="absolute right-0 top-0 h-full w-[5px] cursor-col-resize hover:bg-[var(--border-default)]"
    />
  );
}
